import { Controller } from '@hotwired/stimulus'

const CONFIRMATION_MS = 1200

export default class CopyLinkController extends Controller<HTMLElement> {
  static targets = ['button', 'url', 'tooltip']
  static values = { value: String }

  declare readonly buttonTarget: HTMLButtonElement
  declare readonly hasButtonTarget: boolean
  declare readonly urlTarget: HTMLElement
  declare readonly hasUrlTarget: boolean
  declare readonly tooltipTarget: HTMLElement
  declare readonly hasTooltipTarget: boolean
  declare readonly valueValue: string

  private isConfirming = false
  private hideTooltipTimeout: number | null = null

  selectAll (): void {
    if (!this.hasUrlTarget) {
      return
    }

    const range = document.createRange()

    range.selectNodeContents(this.urlTarget)

    const selection = window.getSelection()

    selection?.removeAllRanges()
    selection?.addRange(range)
  }

  async copy (): Promise<void> {
    if (!await this.write(this.valueValue)) {
      return
    }

    this.confirm()
  }

  // The url itself is the click target here rather than a separate button:
  // selects the text (for a manual copy fallback) and copies it, showing a
  // small "Copied" popup beneath instead of swapping a button's label.
  async selectAndCopy (): Promise<void> {
    this.selectAll()

    if (!await this.write(this.valueValue)) {
      return
    }

    this.showTooltip()
  }

  private async write (text: string): Promise<boolean> {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)

        return true
      } catch {
        return this.writeWithoutClipboardApi(text)
      }
    }

    return this.writeWithoutClipboardApi(text)
  }

  private writeWithoutClipboardApi (text: string): boolean {
    const field = document.createElement('textarea')

    field.value = text
    field.style.position = 'fixed'
    field.style.opacity = '0'

    document.body.appendChild(field)
    field.focus()
    field.select()

    const copied = document.execCommand('copy')

    document.body.removeChild(field)

    return copied
  }

  private confirm (): void {
    if (!this.hasButtonTarget || this.isConfirming) {
      return
    }

    const original = this.buttonTarget.innerHTML
    const label = this.buttonTarget.dataset.copiedLabel

    if (undefined === label) {
      return
    }

    const tick = document.createElement('i')

    tick.className = 'fas fa-check mr-1'
    tick.setAttribute('aria-hidden', 'true')

    this.isConfirming = true
    this.buttonTarget.textContent = ''
    this.buttonTarget.appendChild(tick)
    this.buttonTarget.appendChild(document.createTextNode(label))

    window.setTimeout(() => {
      this.buttonTarget.innerHTML = original
      this.isConfirming = false
    }, CONFIRMATION_MS)
  }

  private showTooltip (): void {
    if (!this.hasTooltipTarget) {
      return
    }

    if (this.hideTooltipTimeout !== null) {
      window.clearTimeout(this.hideTooltipTimeout)
    }

    this.tooltipTarget.hidden = false

    this.hideTooltipTimeout = window.setTimeout(() => {
      this.tooltipTarget.hidden = true
      this.hideTooltipTimeout = null
    }, CONFIRMATION_MS)
  }
}
